import { Divider } from '@material-ui/core';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import { green, red } from '@material-ui/core/colors';
import Paper from '@material-ui/core/Paper';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { darken } from '@material-ui/core/styles/colorManipulator';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Typography from '@material-ui/core/Typography';
import CheckBoxRoundedIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import 'moment-duration-format';
import * as qs from 'qs';
import React, { useContext, useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import WindsorApi from '../../api/WindsorApi';
import LinkRouter from '../../common/LinkRouter';
import LoadingIndicator from '../../common/LoadingIndicator';
import MiddleDot from '../../common/MiddleDot';
import NoResultsText from '../../common/NoResultsText';
import TimestampTooltip from '../../common/TimestampTooltip';
import MobxContext from '../../mobx/MobxContext';
import DeploymentLogsDialog from './DeploymentLogsDialog';
import FaroDeploymentActivityList from './FaroDeploymentActivityList';
import FaroDeploymentAssociationsTable from './FaroDeploymentAssociationsTable';
import FaroDeploymentOutputsTable from './FaroDeploymentOutputsTable';
import FaroDeploymentParametersTable from './FaroDeploymentParametersTable';
import FaroDeploymentResourcesTable from './FaroDeploymentResourcesTable';
import Tooltip from '@material-ui/core/Tooltip';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      maxWidth: 1200,
      margin: 'auto'
    },
    header: {
      marginBottom: 24,
    },
    subtitleTypography: {
      fontWeight: 500
    },
    primaryTitleContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 8
    },
    divider: {
      marginBottom: 16,
      marginTop: 8
    },
    statusPanel: {
      border: `1px solid ${theme.palette.divider}`,
      padding: 16,
      marginBottom: 24,
      [theme.breakpoints.up('md')]: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }
    },
    statusPanelText: {
      marginBottom: 16,
      display: 'flex',
      alignItems: 'center',
      [theme.breakpoints.up('md')]: {
        marginBottom: 0
      }
    },
    tabs: {
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: darken(theme.palette.background.default, 0.03)
    },
    notFoundContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      marginTop: 36
    },
    notFoundPaper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: 24,
      marginBottom: 16
    },
    notFoundTitle: {
      marginBottom: 16
    },
    timestamp: {
      marginLeft: 4,
      marginRight: 4
    },
    deckVersionLink: {
      marginLeft: 4,
      maxWidth: 100,
      width: 100,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis'
    },
    successIcon: {
      color: green[500]
    },
    errorIcon: {
      color: red[500]
    },
    tabContainer: {
      paddingTop: 16
    },
    statusIconContainer: {
      marginRight: 16,
      display: 'flex'
    }
  })
);

export enum DeploymentState {
  InProgress = 'InProgress',
  Passed = 'Passed',
  Failed = 'Failed'
}

const windsorApi = new WindsorApi();

const DEFAULT_TAB = 'activity';
const DEPLOYMENT_SUCCESS_STATES = ['COMPLETE', 'TORN_DOWN'];
const DEPLOYMENT_FAILED_STATES = ['TEARDOWN_FAILED', 'DEPLOYMENT_FAILED', 'UPDATE_FAILED', 'IMPORT_FAILED'];

function FaroDeploymentDetailsPage(props: RouteComponentProps<any>) {
  const { match: { params: { accountId, region, deploymentName } }, location } = props;
  const { global = false } = qs.parse(location.search, { ignoreQueryPrefix: true });
  const classes = useStyles({});
  const [tab, setTab] = useState(DEFAULT_TAB);
  const [deployment, setDeployment] = useState(null);
  const [deploymentState, setDeploymentState] = useState(null);
  const [deploymentEvents, setDeploymentEvents] = useState(null);
  const [deploymentLogs, setDeploymentLogs] = useState(null);
  const [deploymentParameters, setDeploymentParameters] = useState(null);
  const [deploymentOutputs, setDeploymentOutputs] = useState(null);
  const [deploymentDependencies, setDeploymentDependencies] = useState(null);
  const [deploymentDependents, setDeploymentDependents] = useState(null);
  const [deploymentResources, setDeploymentResources] = useState(null);
  const [showDeploymentLogDialog, setShowDeploymentLogDialog] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const { environmentStore, preferenceStore } = useContext(MobxContext);

  const environment = environmentStore.environments ? environmentStore.getEnvironmentByAccountId(accountId) : null;

  useEffect(() => {
    setDeployment(null);
    setTab(DEFAULT_TAB);

    preferenceStore.addRecentlyViewedDeployment(deploymentName, accountId, region, global);

    windsorApi.getFaroDeployment(deploymentName, accountId, region, global)
      .then(res => setDeployment(res))
      .catch(error => {
        if (error.status === 404) {
          setNotFound(true);
        }
      });
  }, [deploymentName, accountId, region, global, preferenceStore]);

  useEffect(() => {
    if (deployment) {
      const scheduleQuery = () => {
        const timeoutHandle = setTimeout(() => windsorApi.getFaroDeployment(deployment.DeploymentName, deployment.AccountId, deployment.Region, deployment.Global)
          .then(res => setDeployment(res))
          .catch(error => {
            if (error.status === 404) {
              setNotFound(true);
            }
          }), 10000);
        return () => clearTimeout(timeoutHandle);
      };


      let deploymentState = DeploymentState.InProgress;
      if (DEPLOYMENT_SUCCESS_STATES.includes(deployment.State)) {
        deploymentState = DeploymentState.Passed;
      } else if (DEPLOYMENT_FAILED_STATES.includes(deployment.State)) {
        deploymentState = DeploymentState.Failed;
      }

      if (!deployment.DeploymentTime.Action) {
        deployment.DeploymentTime.Action = 'DEPLOY';
      }
      const updatesCopy = deployment.Updates ? [deployment.DeploymentTime, ...deployment.Updates] : [deployment.DeploymentTime];

      setDeploymentState(deploymentState);

      setDeploymentEvents(updatesCopy.reverse().map(event => {
        const startTime = moment(event.StartTime).valueOf();
        const endTime = moment(event.EndTime).valueOf();
        const duration = moment.duration(endTime - startTime);
        return {
          action: event.Action || 'UPDATE',
          deckVersion: event.DeckVersion,
          startTime,
          endTime,
          duration: duration.format('HH:mm:ss', { trim: false }),
          user: event.User || 'Unknown User',
          logId: event.Log ? event.Log.ID : undefined
        };
      }));

      setDeploymentParameters(deployment.Parameters ? Object.keys(deployment.Parameters).map(name => ({
        name,
        value: deployment.Parameters[name],
        default: deployment.ParameterMetadata && deployment.ParameterMetadata[name] ? deployment.ParameterMetadata[name].Defaulted : false
      })) : []);

      setDeploymentOutputs(deployment.Outputs ? Object.keys(deployment.Outputs).map(name => ({
        name,
        value: deployment.Outputs[name]
      })) : []);

      setDeploymentDependents(deployment.Dependents ? deployment.Dependents.map(dependent => ({
        deckName: dependent.DeckName,
        deploymentName: dependent.DeploymentName
      })) : []);

      setDeploymentDependencies(deployment.Dependencies ? Object.keys(deployment.Dependencies).map(deckName => ({
        deckName,
        deploymentName: deployment.Dependencies[deckName]
      })) : []);

      const resources = deployment.Resources;
      setDeploymentResources([
        ...resources.Stacks ? resources.Stacks.map(item => ({
          resourceType: 'stack',
          resourceName: item.StackName,
          href: `https://console.aws.amazon.com/cloudformation/home?region=${deployment.Region}#/stacks/stackinfo?stackId=${encodeURIComponent(item.StackID)}`
        })) : [],
        ...resources.S3Objects ? resources.S3Objects.map(item => ({
          resourceType: 's3Object',
          resourceName: `${item.Bucket}/${item.Location}`,
          href: `https://s3.console.aws.amazon.com/s3/object/${item.Bucket}/${item.Location}?region=${deployment.Region}`
        })) : [],
        ...resources.CodeDeployDeployments ? resources.CodeDeployDeployments.map(item => ({
          resourceType: 'codeDeployDeployment',
          resourceName: item.Application,
          href: `https://console.aws.amazon.com/codesuite/codedeploy/applications/${item.Application}?region=${deployment.Region}`
        })) : [],
        ...resources.LambdaFunctions ? resources.LambdaFunctions.map(item => ({
          resourceType: 'lambdaFunction',
          resourceName: item.Name,
          href: `https://console.aws.amazon.com/lambda/home?region=${deployment.Region}#/functions/${item.Name}`
        })) : [],
        ...resources.LambdaLayers ? resources.LambdaLayers.map(item => ({
          resourceType: 'lambdaLayer',
          resourceName: item.Name,
          href: `https://console.aws.amazon.com/lambda/home?region=${deployment.Region}#/layers/${item.Name}/versions/${item.Versions[item.Versions.length - 1].Version}`
        })) : []
      ]);

      if (deploymentState === DeploymentState.InProgress) {
        return scheduleQuery();
      }
    }
  }, [deployment]);

  const onGetLogs = (log_id) => {
    setShowDeploymentLogDialog(true);
    setDeploymentLogs(null);
    windsorApi.getFaroDeploymentLogs(deploymentName, accountId, region, global, log_id).then(res => {
      setDeploymentLogs(res);
    });
  };

  const onCloseDeploymentLogDialog = () => {
    setShowDeploymentLogDialog(false);
  };

  const onTabChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    setTab(newValue);
  };

  if (notFound) {
    return <div>
      <div className={classes.notFoundContainer}>
        <Paper className={classes.notFoundPaper}>
          <Typography variant="h6" className={classes.notFoundTitle} >
            Deployment Not Found
          </Typography>
          <Typography color="textSecondary" variant="h6">
            Deployment <strong>{deploymentName}</strong> not found in account <strong>{accountId}</strong> and region <strong>{region}</strong>
          </Typography>
        </Paper>
      </div>
    </div>;
  } else if (
    deployment &&
    deploymentEvents &&
    deploymentState &&
    deploymentParameters &&
    deploymentOutputs &&
    deploymentDependencies &&
    deploymentDependents &&
    deploymentResources &&
    environment
  ) {
    const events = deploymentState === DeploymentState.InProgress ? deploymentEvents.slice(1) : deploymentEvents;

    return (
      <div className={classes.root}>
        <Breadcrumbs aria-label="breadcrumb">
          <LinkRouter color="textSecondary" to={`/faro/deployments`}>
            Deployments
          </LinkRouter>
          <Typography color="textPrimary">{deploymentName}</Typography>
        </Breadcrumbs>
        <Divider className={classes.divider} />
        <div className={classes.header}>
          <div className={classes.primaryTitleContainer}>
            <Typography variant="h6">Deployment: {deploymentName}</Typography>
          </div>
          <div>
            <Typography className={classes.subtitleTypography} color="primary" display="inline" >
              Environment: {environment.Alias}
            </Typography>
            <MiddleDot />
            <Typography className={classes.subtitleTypography} color="primary" display="inline" >
              Region: {deployment.Region}
            </Typography>
            <MiddleDot />
            <Typography className={classes.subtitleTypography} color="primary" display="inline" >
              Deck: {deployment.DeckName}
            </Typography>
            <MiddleDot />
            <Typography className={classes.subtitleTypography} color="primary" display="inline" >
              Global: {deployment.Global ? 'Yes' : 'No'}
            </Typography>
          </div>
        </div>
        <div className={classes.statusPanel}>
          <div className={classes.statusPanelText}>
            <Tooltip title={deployment.State}>
              <div className={classes.statusIconContainer}>
                {deploymentState === DeploymentState.Passed && <CheckBoxRoundedIcon className={classes.successIcon} />}
                {deploymentState === DeploymentState.Failed && <ErrorIcon className={classes.errorIcon} />}
                {deploymentState === DeploymentState.InProgress && <CircularProgress size={20} />}
              </div>
            </Tooltip>
            <Typography variant="subtitle1">
              {deploymentState === DeploymentState.InProgress ? 'Update started' : 'Last updated'}
            </Typography>
            {deploymentState === DeploymentState.InProgress && <TimestampTooltip timestamp={deploymentEvents[0].startTime}>
              <Typography
                className={classes.timestamp}
                component="span"
                display="inline"
                variant="subtitle1"
              >
                {moment(deploymentEvents[0].startTime).fromNow()}
              </Typography>
            </TimestampTooltip>}
            {deploymentState !== DeploymentState.InProgress && <TimestampTooltip timestamp={deploymentEvents[0].endTime}>
              <Typography
                className={classes.timestamp}
                component="span"
                display="inline"
                variant="subtitle1"
              >
                {moment(deploymentEvents[0].endTime).fromNow()}
              </Typography>
            </TimestampTooltip>}
            <Typography variant="subtitle1">
              with deck version
            </Typography>
            <LinkRouter
              className={classes.deckVersionLink}
              color="primary"
              variant="subtitle1"
              to={`/faro/decks/${deployment.DeckName}?version=${encodeURIComponent(deploymentEvents[0].deckVersion)}`}>
              {deploymentEvents[0].deckVersion}
            </LinkRouter>
          </div>
          {deploymentEvents[0].logId && <Button variant="outlined" size="small" color="primary" onClick={() => onGetLogs(deploymentEvents[0].logId)}>View Logs</Button>}
        </div>
        <Tabs
          className={classes.tabs}
          value={tab}
          onChange={onTabChange}
          aria-label="Parameter Set Details Tabs"
          indicatorColor="primary"
          textColor="primary"
          scrollButtons="auto"
          variant="scrollable"
        >
          <Tab label="Activity" value="activity" />
          <Tab label="Resources" value="resources" />
          <Tab label="Parameters" value="parameters" />
          <Tab label="Outputs" value="outputs" />
          <Tab label="Dependents" value="dependents" />
          <Tab label="Dependencies" value="dependencies" />
        </Tabs>
        {tab === 'activity' && <div>
          {events.length > 0 && <FaroDeploymentActivityList events={events} deployment={deployment} onShowLogs={onGetLogs} />}
          {events.length === 0 && <NoResultsText>Activity will be displayed after deployment is completed</NoResultsText>}
        </div>}
        {tab === 'resources' && <div className={classes.tabContainer}>
          {deploymentResources.length > 0 && <Paper><FaroDeploymentResourcesTable rows={deploymentResources} /></Paper>}
          {deploymentResources.length === 0 && <NoResultsText>No Resources</NoResultsText>}
        </div>}
        {tab === 'parameters' && <div className={classes.tabContainer}>
          {deploymentParameters.length > 0 && <Paper><FaroDeploymentParametersTable rows={deploymentParameters} /></Paper>}
          {deploymentParameters.length === 0 && <NoResultsText>No Parameters</NoResultsText>}
        </div>}
        {tab === 'outputs' && <div className={classes.tabContainer}>
          {deploymentOutputs.length > 0 && <Paper><FaroDeploymentOutputsTable rows={deploymentOutputs} /></Paper>}
          {deploymentOutputs.length === 0 && <NoResultsText>No Outputs</NoResultsText>}
        </div>}
        {tab === 'dependents' && <div className={classes.tabContainer}>
          {deploymentDependents.length > 0 && <Paper><FaroDeploymentAssociationsTable rows={deploymentDependents} accountId={accountId} region={region} /></Paper>}
          {deploymentDependents.length === 0 && <NoResultsText>No Dependents</NoResultsText>}
        </div>}
        {tab === 'dependencies' && <div className={classes.tabContainer}>
          {deploymentDependencies.length > 0 && <Paper><FaroDeploymentAssociationsTable rows={deploymentDependencies} accountId={accountId} region={region} /></Paper>}
          {deploymentDependencies.length === 0 && <NoResultsText>No Dependencies</NoResultsText>}
        </div>}
        {showDeploymentLogDialog && <DeploymentLogsDialog logs={deploymentLogs} onClose={onCloseDeploymentLogDialog} />}
      </div>
    );
  } else {
    return <LoadingIndicator loading={true} />;
  }

};

export default withRouter(observer(FaroDeploymentDetailsPage));
